import { nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useCommonStore } from '@/stores/common.ts'
import { useEditorStore } from '@/stores/editor.ts'
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { useSelectorOverlay } from '@/views/editor/composable/table/selector/selctorOverlay.ts'
import { usePaste } from '@/views/editor/composable/usePaste.ts'
import { useCopy, setSelectAllActive } from '@/views/editor/composable/useCopy.ts'
import { useImage } from '@/views/editor/composable/image/useImage.ts'
import { applyFontStyle } from '@/views/editor/composable/applyFontStyle'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { deletePrefix } from './useNoteUtils'
import { directSocketSave } from './useSocketSave'
import { createBlock } from './useBlockCreate'
import { deleteButtonEvent } from './useBlockDelete'
import { calcFunction } from '@/views/editor/composable/table/useFunction.ts'

const useDelete = useDeleteTable()
const useSelectorOver = useSelectorOverlay()
const uImage = useImage()
const { onPaste } = usePaste()
const { onCopy, onCut } = useCopy()

/**
 * 블록 관련 키보드 이벤트 처리
 * @param e 키보드 이벤트
 */
export const blockEvent = async (e: any) => {
	const target = e.target as HTMLElement
	const isInTable = target?.closest('td')

	if (e.key === 'Enter') {
		// IME 조합 중에는 Enter 처리 건너뜀 (한글 등 조합형 문자 입력 중)
		if (e.isComposing) {
			return
		}

		// 테이블 안에서는 테이블 로직이 처리
		if (isInTable) return

		// Shift+Enter: 줄바꿈 (브라우저 기본 동작 사용)
		if (e.shiftKey) {
			// preventDefault 하지 않아 브라우저가 자동으로 <br> 삽입
			const history = new UseHistory()
			setTimeout(async () => {
				await history.push()
			}, 0)
			return
		}

		// 일반 Enter: 새 블록 생성
		e.preventDefault()

		const selection = window.getSelection()
		if (selection && !selection.isCollapsed) {
			selection.deleteFromDocument()
		}

		const history = new UseHistory()
		await history.push()

		await createBlock()

		await new Promise(resolve => setTimeout(resolve, 50))
		new UseSelection().setChangeSelection()
		await history.push()
	} else if (e.key === 'Backspace' || e.key === 'Delete') {
		if (isInTable) {
			const sel = window.getSelection()
			let isCrossBlock = false
			if (sel && sel.rangeCount > 0 && !sel.getRangeAt(0).collapsed) {
				const r = sel.getRangeAt(0)
				const sEl = r.startContainer.nodeType === Node.TEXT_NODE ? r.startContainer.parentElement : (r.startContainer as HTMLElement)
				const eEl = r.endContainer.nodeType === Node.TEXT_NODE ? r.endContainer.parentElement : (r.endContainer as HTMLElement)
				const sBlock = sEl?.closest('.editor--block')
				const eBlock = eEl?.closest('.editor--block')
				isCrossBlock = !!(sBlock && eBlock && sBlock !== eBlock)
			}
			if (!isCrossBlock) {
				const content = target.textContent || ''
				if (content.trim() === '') {
					e.preventDefault()
					e.stopPropagation()
				}
				return
			}
		}
		await deleteButtonEvent(e)
	} else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
		await keyArrowEvent(e)
	} else if (e.ctrlKey || e.metaKey) {
		// 단축키 처리 (Bold, Italic, Underline)
		switch (e.key) {
			case 'b':
			case 'B': {
				e.preventDefault()
				e.stopPropagation()
				const hB = new UseHistory()
				await hB.push(false, { preserveSelectionOnUndo: true, fontStyleApplied: true })
				await applyFontStyle('bold')
				await nextTick()
				await hB.push(true, { fontStyleApplied: true, preserveSelectionOnUndo: true })
				break
			}

			case 'i':
			case 'I': {
				e.preventDefault()
				e.stopPropagation()
				const hI = new UseHistory()
				await hI.push(false, { preserveSelectionOnUndo: true, fontStyleApplied: true })
				await applyFontStyle('italic')
				await nextTick()
				await hI.push(true, { fontStyleApplied: true, preserveSelectionOnUndo: true })
				break
			}

			case 'u':
			case 'U': {
				e.preventDefault()
				e.stopPropagation()
				const hU = new UseHistory()
				await hU.push(false, { preserveSelectionOnUndo: true, fontStyleApplied: true })
				await applyFontStyle('underline')
				await nextTick()
				await hU.push(true, { fontStyleApplied: true, preserveSelectionOnUndo: true })
				break
			}

			case 's':
			case 'S':
				if (e.shiftKey) {
					e.preventDefault()
					e.stopPropagation()
					const hS = new UseHistory()
					await hS.push(false, { preserveSelectionOnUndo: true, fontStyleApplied: true })
					await applyFontStyle('line-through')
					await nextTick()
					await hS.push(true, { fontStyleApplied: true, preserveSelectionOnUndo: true })
				}
				break

			case 'a':
			case 'A':
				setSelectAllActive()
				break
		}
	}
}

/**
 * 기본 이벤트 방지
 */
export const eventDefault = (e: any) => {
	e.preventDefault()
	e.stopPropagation()
	return
}

/**
 * IME 조합 중에는 기본 이벤트 방지를 건너뛰는 keyup 핸들러
 */
const keyupDefault = (e: KeyboardEvent) => {
	if (e.isComposing) return
	e.preventDefault()
	e.stopPropagation()
}

// #editor 레벨 전역 paste 차단 핸들러
const globalPasteHandler = (e: ClipboardEvent) => {
	const target = e.target as HTMLElement
	const block = target.closest('.editor--block') as HTMLElement

	// 이미지/파일 블록이고, HTML 데이터가 클립보드에 있으면 차단 (외부 텍스트 붙여넣기)
	// 하지만 전체 선택 복사(Ctrl+A)나 블록 자체 복사는 허용
	// 캡션 영역은 붙여넣기 허용
	if (block && (block.dataset.type === 'image' || block.dataset.type === 'file')) {
		const isCaption = target.closest('.image--caption__title') || target.closest('.image--caption__description')
		if (isCaption) return

		const clipboardData = e.clipboardData
		const hasHTML = clipboardData?.types.includes('text/html')
		const htmlContent = clipboardData?.getData('text/html') || ''

		// editor--block__container가 포함되어 있으면 우리가 복사한 블록 → 허용
		if (htmlContent.includes('editor--block__container') || htmlContent.includes('editor--block')) {
			return
		}

		// 순수 텍스트만 있으면 차단
		if (hasHTML || clipboardData?.types.includes('text/plain')) {
			e.preventDefault()
			e.stopPropagation()
			e.stopImmediatePropagation()
		}
	}
}

const globalBeforeInputHandler = (e: InputEvent) => {
	const target = e.target as HTMLElement
	const block = target.closest('.editor--block') as HTMLElement

	// 이미지/파일 블록에서 Enter, 타이핑 등 모든 입력 차단
	// 단, 삭제(deleteContentBackward)는 허용 (Backspace로 블록 삭제 가능하게)
	// 캡션 영역(image--caption__title, image--caption__description)은 입력 허용
	if (block && (block.dataset.type === 'image' || block.dataset.type === 'file')) {
		const isCaption = target.closest('.image--caption__title') || target.closest('.image--caption__description')
		if (isCaption) return

		if (e.inputType !== 'deleteContentBackward' && e.inputType !== 'deleteContentForward') {
			e.preventDefault()
			e.stopPropagation()
			e.stopImmediatePropagation()
		}
	}
}

const preventDragStart = (e: DragEvent) => {
	e.preventDefault()
}

export const newBlockEvent = () => {
	const { isWrite } = storeToRefs(useEditorStore())

	if (!isWrite.value) return

	const container: any = document.getElementById('editor')
	const blockList = [...container.querySelectorAll('.editor--block')]

	// #editor 레벨에서 paste/beforeinput 전역 차단 (캡처 단계, 중복 제거 후 등록)
	container.removeEventListener('paste', globalPasteHandler, true)
	container.removeEventListener('beforeinput', globalBeforeInputHandler, true)
	container.addEventListener('paste', globalPasteHandler, true)
	container.addEventListener('beforeinput', globalBeforeInputHandler, true)

	// container 레벨에서 한 번만 이벤트 등록 (중복 방지를 위해 Set 사용)
	const processedContainers = new Set()

	blockList.forEach((block: any) => {
		const blockContainer = block.closest('.editor--block__container')

		// container 레벨에서 이벤트 등록 (모든 블록 타입에 대해)
		if (blockContainer && !processedContainers.has(blockContainer)) {
			processedContainers.add(blockContainer)

			blockContainer.removeEventListener('mouseenter', useSelectorOver.handlerPosition)
			blockContainer.removeEventListener('mouseleave', useSelectorOver.disabledHandlerPosition)
			blockContainer.addEventListener('mouseenter', useSelectorOver.handlerPosition)
			blockContainer.addEventListener('mouseleave', useSelectorOver.disabledHandlerPosition)
		}
		block.removeEventListener('dragstart', preventDragStart)
		block.addEventListener('dragstart', preventDragStart)

		// click 이벤트 추가 - 클릭 시 포커스 강제
		// 테이블 블록은 tableClickDelegate에서 처리하므로 제외
		if (block.dataset.type !== 'defaultTable') {
			block.removeEventListener('click', handleBlockClick)
			block.addEventListener('click', handleBlockClick)
		}

		// 파일 블록: mousedown 시 브라우저 기본 선택 동작 차단
		if (block.dataset.type === 'file') {
			block.removeEventListener('mousedown', handleFileBlockMousedown)
			block.addEventListener('mousedown', handleFileBlockMousedown)
		}

		// focusin 이벤트는 블록에 직접 등록
		if (block.dataset.type === 'text') {
			block.removeEventListener('focusin', focusInBlockEvent)
			block.removeEventListener('focusout', focusOutBlockEvent)
			block.addEventListener('focusin', focusInBlockEvent)
			block.addEventListener('focusout', focusOutBlockEvent)
		}
		// focusout은 document 레벨에서 처리하므로 블록에는 등록하지 않음

		if (block.dataset.type === 'defaultTable') {
			block.removeEventListener('focusout', focusOutTableEvent)
			block.addEventListener('focusout', focusOutTableEvent)
		}

		// copy/paste/cut 이벤트는 모든 블록 타입에 등록
		if (block.dataset.type === 'text' || block.dataset.type === 'defaultTable' || block.dataset.type === 'image' || block.dataset.type === 'file') {
			block.removeEventListener('copy', onCopy as any)
			block.removeEventListener('cut', onCut as any)
			block.addEventListener('copy', onCopy as any)
			block.addEventListener('cut', onCut as any)
		}

		// keyup, paste는 텍스트 블록에만 등록
		const hasTable = block.querySelector('table') !== null
		if (block.dataset.type === 'text' && !hasTable) {
			block.removeEventListener('keyup', keyupDefault)
			block.removeEventListener('paste', onPaste as any)
			block.addEventListener('keyup', keyupDefault)
			block.addEventListener('paste', onPaste as any)
		}

		if (block.dataset.type === 'image') {
			block.removeEventListener('mouseover', uImage.init)
			block.removeEventListener('mouseleave', useImage().deleteSizeElement)

			block.addEventListener('mouseover', uImage.init)
			block.addEventListener('mouseleave', useImage().deleteSizeElement)
		}
	})
}

/**
 * 파일 블록 mousedown 처리 - contenteditable="false" 요소에 대한 브라우저 기본 전체선택 방지
 */
function handleFileBlockMousedown(e: MouseEvent) {
	e.preventDefault()
}

/**
 * 블록 클릭 처리
 */
function handleBlockClick(e: any) {
	const block = e.currentTarget
	if (!block) return

	if (!block.isConnected) return

	// 테이블 블록은 tableClickDelegate에서 처리하므로 여기서는 무시
	if (block.dataset.type === 'defaultTable') {
		return
	}

	// selector 업데이트
	const { selector } = storeToRefs(useEditorStore())
	selector.value = block

	// 텍스트 블록인 경우에만 active 클래스 처리
	if (block.dataset.type === 'text') {
		// 모든 블록의 active 클래스 제거
		const editorBlockList = [...document.querySelectorAll('.editor--block')]
		editorBlockList.forEach((b: any) => {
			b.classList.remove('active')
		})

		// 클릭한 블록에 active 추가
		block.classList.add('active')
	}

	// 이미지 블록 클릭 시 전체 선택 (캡션 영역 클릭은 제외)
	if (block.dataset.type === 'image') {
		const target = e.target as HTMLElement
		const isCaption = target.closest('.image--caption__title') || target.closest('.image--caption__description')
		if (!isCaption) {
			const selection = window.getSelection()
			const range = document.createRange()
			range.selectNodeContents(block)
			selection?.removeAllRanges()
			selection?.addRange(range)
		}
	}

	// 포커스 처리 (이미지 블록 제외)
	if (block.dataset.type !== 'image') {
		if (document.activeElement !== block) {
		block.focus()
		}
	}
}

/**
 * 블록 포커스 진입 시 처리
 */
const focusInBlockEvent = (e: any) => {
	e.stopPropagation()

	const currentBlock = e.currentTarget

	// 테이블 블록 내부의 셀 focus인 경우 selector를 삭제하지 않음
	const isTableCell = e.target && e.target.closest && e.target.closest('td')
	const isTableBlock = currentBlock && currentBlock.getAttribute('data-type') === 'defaultTable'

	if (!(isTableBlock && isTableCell)) {
		useDelete.selector()
	}

	const { socket } = storeToRefs(useCommonStore())
	const { selector, noteType, noteUid } = storeToRefs(useEditorStore())
	const { relatedTarget } = e

	// 이전 선택된 블록과 다른 경우 Blur 처리 및 저장
	if (selector.value && currentBlock.id !== selector.value.id) {
		const prevBlock = selector.value
		socket.value.blockBlur(noteUid.value, deletePrefix(prevBlock.id))

		const isTableBlock = prevBlock.dataset?.type === 'defaultTable'
		if (noteType.value === 'Project' && prevBlock.isConnected && !prevBlock.querySelector('.file--container') && !isTableBlock) {
			directSocketSave('blur', prevBlock)
		}
	}

	// 여러 블록이 선택된 경우 active 클래스를 유지
	const selection = window.getSelection()
	let isMultiBlockSelection = false

	if (selection && selection.rangeCount > 0) {
		const range = selection.getRangeAt(0)
		const startEl: any = range.startContainer.nodeType === Node.TEXT_NODE ? range.startContainer.parentElement : range.startContainer
		const endEl: any = range.endContainer.nodeType === Node.TEXT_NODE ? range.endContainer.parentElement : range.endContainer
		const startBlock = startEl?.closest('.editor--block')
		const endBlock = endEl?.closest('.editor--block')

		// 시작 블록과 끝 블록이 다르면 여러 블록 선택
		if (startBlock && endBlock && startBlock !== endBlock) {
			isMultiBlockSelection = true
		}
	}

	// Undo/Redo 진행 중이 아닐 때만 active 클래스 조작
	const editorStore = useEditorStore()
	const isUndoRedoInProgress = editorStore.isUndoRedoInProgress

	if (!isUndoRedoInProgress.value) {
		// 단일 블록 포커스인 경우에만 active 클래스 초기화
		if (!isMultiBlockSelection) {
			const editorBlockList = [...document.querySelectorAll('.editor--block')]
			editorBlockList.forEach((block: any) => {
				block.classList.remove('active')
			})
		}
	}

	// 현재 블록을 selector로 설정 (항상 실행)
	selector.value = currentBlock

	// 테이블이 아닌 블록으로 이동 시 cellTarget 초기화
	const { cellTarget } = storeToRefs(useEditorStore())
	if (currentBlock.dataset.type !== 'defaultTable') {
		cellTarget.value = null
	}

	if (!isUndoRedoInProgress.value) {
		// 텍스트 블록이고, 다중 블록 선택이 아닐 때만 active 추가
		// 테이블 블록에는 절대 active를 추가하지 않음
		if (selector.value.dataset.type === 'text' && !isMultiBlockSelection) {
			selector.value.classList.add('active')
		}
	}
}

/**
 * 테이블 블록 포커스 해제 시 처리
 */
let _lastTableFocusOutBlockId: string | null = null
let _lastTableFocusOutTime = 0
const focusOutTableEvent = (e: any) => {
	const currentBlock = e.currentTarget
	const relatedTarget = e.relatedTarget as HTMLElement | null

	if (relatedTarget && currentBlock.contains(relatedTarget)) {
		return
	}

	const now = Date.now()
	if (_lastTableFocusOutBlockId === currentBlock.id && now - _lastTableFocusOutTime < 300) {
		return
	}
	_lastTableFocusOutBlockId = currentBlock.id
	_lastTableFocusOutTime = now

	calcFunction()

	useDelete.selector()

	const { socket } = storeToRefs(useCommonStore())
	const { noteUid, noteType } = storeToRefs(useEditorStore())
	const blockUid = deletePrefix(currentBlock.id)

	if (socket.value) {
		socket.value.blockEditEnd(noteUid.value, blockUid)
		socket.value.blockBlur(noteUid.value, blockUid)
	}
	if (noteType.value === 'Project') {
		directSocketSave('blur', currentBlock)
	}
}

/**
 * 블록 포커스 해제 시 처리
 */
let focusOutProcessing = false
let lastFocusOutBlock: string | null = null
const focusOutBlockEvent = (e: any) => {
	e.stopPropagation()

	const currentBlock = e.currentTarget
	const blockId = currentBlock?.id

	// DOM에서 제거된 블록의 focusout은 무시 (삭제/병합 시 비동기 focusout 방지)
	if (!currentBlock.isConnected) {
		return
	}

	// 같은 블록에서 연속으로 focusout이 발생하면 무시
	if (focusOutProcessing && lastFocusOutBlock === blockId) {
		return
	}

	focusOutProcessing = true
	lastFocusOutBlock = blockId

	const { socket } = storeToRefs(useCommonStore())
	const { noteUid, noteType } = storeToRefs(useEditorStore())
	const blockUid = deletePrefix(currentBlock.id)

	// focusout 발생 시 blockEditEnd를 무조건 즉시 호출
	if (socket.value) {
		socket.value.blockEditEnd(noteUid.value, blockUid)
	}

	const relatedTarget = e.relatedTarget as HTMLElement | null
	const movingToEditorBlock = relatedTarget?.closest('.editor--block')

	// 다른 에디터 블록으로 이동하는 경우 active 제거 스킵
	// (focusInBlockEvent에서 active 관리를 처리함)
	if (movingToEditorBlock) {
		if (currentBlock.querySelector('.file--container')) {
			focusOutProcessing = false
			lastFocusOutBlock = null
			return
		}

		if (noteType.value === 'Project') {
			socket.value.blockBlur(noteUid.value, blockUid)
			directSocketSave('blur', currentBlock)
		}

		setTimeout(() => {
			focusOutProcessing = false
			lastFocusOutBlock = null
		}, 100)
		return
	}

	// relatedTarget이 null이거나 에디터 블록 외부로 이동하는 경우
	// relatedTarget이 #editor 컨테이너인 경우도 포함 (중첩 contenteditable → 사이드바 클릭 시 발생)
	// IME 조합, DOM 조작(normalizeBlock 등)으로 인한 일시적 focusout일 수 있으므로
	// 다음 프레임에서 실제로 블록을 떠났는지 확인 후 처리
	requestAnimationFrame(() => {
		if (!currentBlock.isConnected) {
			focusOutProcessing = false
			lastFocusOutBlock = null
			return
		}

		const activeEl = document.activeElement
		const isStillInBlock = activeEl === currentBlock || currentBlock.contains(activeEl) || (activeEl?.id === 'editor' && (activeEl as HTMLElement).contains(currentBlock))

		if (isStillInBlock) {
			focusOutProcessing = false
			lastFocusOutBlock = null
			return
		}

		const blockElementList = [...document.querySelectorAll('.editor--block')]
		blockElementList.forEach((block: any) => {
			block.classList.remove('active')
		})

		if (currentBlock.querySelector('.file--container')) {
			focusOutProcessing = false
			lastFocusOutBlock = null
			return
		}

		if (noteType.value === 'Project') {
			socket.value.blockBlur(noteUid.value, blockUid)
			directSocketSave('blur', currentBlock)
		}

		focusOutProcessing = false
		lastFocusOutBlock = null
	})
}

/**
 * 화살표 키 이동 처리
 */
const keyArrowEvent = async (e: any) => {
	const { selector } = storeToRefs(useEditorStore())

	if (selector.value.dataset.type === 'defaultTable') return

	const container: any = document.getElementById('editor')
	const blockList = [...container.querySelectorAll('.editor--block')]
	const index = blockList.indexOf(selector.value)

	const selection = new UseSelection()
	selection.setElement(selector.value)

	switch (e.key) {
		case 'ArrowUp':
			if (selection.isCursorOnFirstLine(selector.value)) {
				e.preventDefault()
				if (index > 0) {
					const blockPrev = blockList[index - 1]
					selection.setCursorOffset()
					selection.changeBlockFocus(blockPrev)
					selection.moveCursorToEnd()
					selector.value = blockPrev
					updateActiveClass(blockPrev)
				}
			} else {
				setTimeout(() => {
					const newSelection = window.getSelection()
					if (newSelection && newSelection.rangeCount > 0) {
						const range = newSelection.getRangeAt(0)
						const newBlock =
							range.startContainer.nodeType === Node.TEXT_NODE
								? range.startContainer.parentElement?.closest('.editor--block')
								: (range.startContainer as HTMLElement).closest('.editor--block')
						if (newBlock && newBlock !== selector.value) {
							selector.value = newBlock as HTMLElement
							updateActiveClass(newBlock as HTMLElement)
						}
					}
				}, 0)
			}
			break

		case 'ArrowDown':
			if (selection.isCursorOnLastLine(selector.value)) {
				e.preventDefault()
				if (blockList.length - 1 > index) {
					const blockNext = blockList[index + 1]
					selection.setCursorOffset()
					selection.changeBlockFocus(blockNext)
					if (blockNext.innerHTML.includes('<br>')) {
						selection.moveCursorToStart()
					} else {
						selection.moveCursorToEnd()
					}
					selector.value = blockNext
					updateActiveClass(blockNext)
				}
			} else {
				setTimeout(() => {
					const newSelection = window.getSelection()
					if (newSelection && newSelection.rangeCount > 0) {
						const range = newSelection.getRangeAt(0)
						const newBlock =
							range.startContainer.nodeType === Node.TEXT_NODE
								? range.startContainer.parentElement?.closest('.editor--block')
								: (range.startContainer as HTMLElement).closest('.editor--block')
						if (newBlock && newBlock !== selector.value) {
							selector.value = newBlock as HTMLElement
							updateActiveClass(newBlock as HTMLElement)
						}
					}
				}, 0)
			}
			break

		case 'ArrowLeft':
			const isFirst = selection.isCursorAtAbsoluteStart()
			if (isFirst) {
				const blockPrev = blockList[index - 1]
				selection.setCursorOffset()
				selection.changeBlockFocus(blockPrev)
				selection.moveCursorToEnd()
				e.preventDefault()
				selector.value = blockPrev
				updateActiveClass(blockPrev)
			} else {
				// 기본 동작 후 active 업데이트 확인
				setTimeout(() => {
					const newSelection = window.getSelection()
					if (newSelection && newSelection.rangeCount > 0) {
						const range = newSelection.getRangeAt(0)
						const newBlock =
							range.startContainer.nodeType === Node.TEXT_NODE
								? range.startContainer.parentElement?.closest('.editor--block')
								: (range.startContainer as HTMLElement).closest('.editor--block')

						if (newBlock && newBlock !== selector.value) {
							selector.value = newBlock as HTMLElement
							updateActiveClass(newBlock as HTMLElement)
						}
					}
				}, 0)
			}
			break

		case 'ArrowRight':
			const isLast = selection.isCursorAtBlockEnd(selector.value)
			if (isLast) {
				const blockPrev = blockList[index + 1]
				selection.setCursorOffset()
				selection.changeBlockFocus(blockPrev)
				selection.moveCursorToEnd(true)
				e.preventDefault()
				selector.value = blockPrev
				updateActiveClass(blockPrev)
			} else {
				// 기본 동작 후 active 업데이트 확인
				setTimeout(() => {
					const newSelection = window.getSelection()
					if (newSelection && newSelection.rangeCount > 0) {
						const range = newSelection.getRangeAt(0)
						const newBlock =
							range.startContainer.nodeType === Node.TEXT_NODE
								? range.startContainer.parentElement?.closest('.editor--block')
								: (range.startContainer as HTMLElement).closest('.editor--block')

						if (newBlock && newBlock !== selector.value) {
							selector.value = newBlock as HTMLElement
							updateActiveClass(newBlock as HTMLElement)
						}
					}
				}, 0)
			}
			break
	}
}

/**
 * active 클래스 업데이트 (텍스트 블록에만 적용)
 */
const updateActiveClass = (targetBlock: HTMLElement) => {
	if (!targetBlock || targetBlock.dataset.type !== 'text') {
		return
	}

	const editorBlockList = [...document.querySelectorAll('.editor--block')]
	editorBlockList.forEach((block: any) => {
		block.classList.remove('active')
	})

	targetBlock.classList.add('active')
}
